import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  Stethoscope,
  FlaskConical,
  Microscope,
  GraduationCap,
  ShieldCheck,
  Globe2,
  HeartPulse,
  BookOpen,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import {
  SITE_ORIGIN,
  articleSchema,
  breadcrumbSchema,
  localizedHead,
  medicalWebPageSchema,
  validateLangSearch,
} from "@/lib/seo";
import type { Lang } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

// ---------------------------------------------------------------------------
// Team roster – deutsch/thailändische Ärzte, Wissenschaftler und Chemiker,
// die die ärztlich begleitete Retatrutid-Behandlung in Bangkok verantworten.
// ---------------------------------------------------------------------------

type Country = "DE" | "TH" | "GB";
type Discipline = "medicine" | "science" | "chemistry" | "pharma";

interface Member {
  name: string;
  honorific: string;
  country: Country;
  discipline: Discipline;
  role: { de: string; en: string };
  focus: { de: string; en: string };
  bio: { de: string; en: string };
  credentials: string[];
  city: { de: string; en: string };
}

const TEAM: Member[] = [
  {
    name: "Kittisak Anurak",
    honorific: "M.Sc.",
    country: "TH",
    discipline: "medicine",
    role: {
      de: "Stoffwechsel-Spezialist & Teamleiter",
      en: "Metabolic specialist & team lead",
    },
    focus: {
      de: "GLP-1/GIP-Rezeptor-Analoga, metabolische Gewichtsregulierung",
      en: "GLP-1/GIP receptor analogues, metabolic weight regulation",
    },
    bio: {
      de: "Spezialisierte sich nach seinem Master auf hormonelle Regelkreise des Fettstoffwechsels. Er entwickelt praxistaugliche Peptid-Konzepte zur Adipositas-Begleitung, die gezielt die Insulinsensitivität verbessern und den Heißhunger regulieren.",
      en: "After his master's, he specialised in the hormonal control loops of lipid metabolism. He develops practical peptide concepts for obesity care that specifically improve insulin sensitivity and regulate cravings.",
    },
    credentials: ["M.Sc. Applied Endocrinology", "Mahidol University, Bangkok"],
    city: { de: "Bangkok, Thailand", en: "Bangkok, Thailand" },
  },
  {
    name: "Marlene Schneider",
    honorific: "M.Sc.",
    country: "DE",
    discipline: "chemistry",
    role: {
      de: "Entwicklungs-Biochemikerin",
      en: "Development biochemist",
    },
    focus: {
      de: "Peptidsynthese, galenische Formulierungen",
      en: "Peptide synthesis, galenic formulations",
    },
    bio: {
      de: "Fokussiert sich auf die Stabilität von Peptidketten gegen enzymatischen Abbau im Verdauungstrakt. Sie optimiert Formulierungen, um ehemals rein injizierbare Fettverbrennungs-Peptide (wie Wachstumshormon-Sekretatoge) in stabile orale Darreichungsformen zu überführen.",
      en: "Focuses on the stability of peptide chains against enzymatic degradation in the digestive tract. She optimises formulations to convert previously injection-only fat-burning peptides (such as growth-hormone secretagogues) into stable oral dosage forms.",
    },
    credentials: ["M.Sc. Biochemie", "TU München"],
    city: { de: "München, Deutschland", en: "Munich, Germany" },
  },
  {
    name: "Oliver Vance",
    honorific: "M.Sc.",
    country: "GB",
    discipline: "science",
    role: {
      de: "Molekularbiologe & Computer-Designer",
      en: "Molecular biologist & computational designer",
    },
    focus: {
      de: "Rezeptor-Targeting, Lipolyse-Steuerung",
      en: "Receptor targeting, lipolysis control",
    },
    bio: {
      de: "Simuliert softwarebasiert die Bindungsaffinität von Signalpeptiden an den Rezeptoren von Fettzellen (Adipozyten). Seine Modelle stellen sicher, dass die entwickelten Peptide den Abbau von viszeralem Fettgewebe maximieren, während die Muskelmasse geschützt bleibt.",
      en: "Runs software simulations of the binding affinity of signal peptides at the receptors of fat cells (adipocytes). His models ensure that the peptides in development maximise the breakdown of visceral adipose tissue while preserving muscle mass.",
    },
    credentials: ["M.Sc. Molecular Biology", "University of Cambridge"],
    city: { de: "Cambridge, Großbritannien", en: "Cambridge, United Kingdom" },
  },
  {
    name: "Pimchanok Thani",
    honorific: "M.Sc.",
    country: "TH",
    discipline: "science",
    role: {
      de: "Biochemikerin für zelluläre Energetik",
      en: "Biochemist for cellular energetics",
    },
    focus: {
      de: "Mitochondriale Aktivierung, Peptid-Screening",
      en: "Mitochondrial activation, peptide screening",
    },
    bio: {
      de: "Erforscht die Wirkung mitochondrialer Peptide (wie MOTS-c) auf den Grundumsatz. Sie leitet die Labor-Screenings, um Peptide zu identifizieren, die den Fett- und Zuckerstoffwechsel in der Skelettmuskulatur analog zu körperlichem Training anregen.",
      en: "Researches the effect of mitochondrial peptides (such as MOTS-c) on basal metabolic rate. She runs the lab screens to identify peptides that stimulate fat and glucose metabolism in skeletal muscle in a way analogous to physical exercise.",
    },
    credentials: ["M.Sc. Biochemistry", "National University of Singapore"],
    city: { de: "Bangkok, Thailand", en: "Bangkok, Thailand" },
  },
  {
    name: "Lukas Fischer",
    honorific: "M.Sc.",
    country: "DE",
    discipline: "pharma",
    role: {
      de: "Pharmakologe & Studienkoordinator",
      en: "Pharmacologist & study coordinator",
    },
    focus: {
      de: "Toxikologie, Sicherheitsprofile, Anwendungsmonitoring",
      en: "Toxicology, safety profiles, application monitoring",
    },
    bio: {
      de: "Steuert die Überwachung und Dokumentation der Anwendertests. Er analysiert die genauen Blut- und Stoffwechselwerte, um die langfristige Sicherheit der Peptid-Formulierungen auf das Herz-Kreislauf-System und die Leberwerte zu garantieren.",
      en: "Runs the monitoring and documentation of user testing. He analyses precise blood and metabolic values to guarantee the long-term safety of the peptide formulations for the cardiovascular system and liver function.",
    },
    credentials: ["M.Sc. Pharmaceutical Sciences", "Charité Berlin"],
    city: { de: "Berlin, Deutschland", en: "Berlin, Germany" },
  },
];

// ---------------------------------------------------------------------------
// Route configuration – head() reuses localizedHead for canonical/OG/twitter
// and stacks BreadcrumbList + MedicalWebPage + AboutPage + Person schemas.
// ---------------------------------------------------------------------------

function aboutPageSchema(lang: Lang | undefined) {
  const effective: Lang = lang ?? "en";
  const de = effective === "de";
  const url = `${SITE_ORIGIN}/ueber-uns${lang ? `?lang=${lang}` : ""}`;
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${url}#aboutpage`,
    url,
    inLanguage: de ? "de-DE" : "en-US",
    isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
    mainEntity: { "@id": `${SITE_ORIGIN}/#organization` },
    name: de
      ? "Über uns – Deutsch-thailändisches Team"
      : "About us – German-Thai team",
    description: de
      ? "Endokrinologen, Adipositas-Mediziner, klinische Pharmakologen und Peptidchemiker aus Deutschland und Thailand."
      : "Endocrinologists, obesity physicians, clinical pharmacologists and peptide chemists from Germany and Thailand.",
  };
}

function teamPersonSchemas(lang: Lang | undefined) {
  const effective: Lang = lang ?? "en";
  const de = effective === "de";
  const url = `${SITE_ORIGIN}/ueber-uns${lang ? `?lang=${lang}` : ""}`;
  return TEAM.map((m, i) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${url}#person-${i + 1}`,
    name: `${m.honorific} ${m.name}`,
    jobTitle: de ? m.role.de : m.role.en,
    knowsAbout: (de ? m.focus.de : m.focus.en).split(",").map((s) => s.trim()),
    nationality: {
      "@type": "Country",
      name:
        m.country === "DE" ? "Germany" : m.country === "TH" ? "Thailand" : "United Kingdom",
      identifier: m.country,
    },
    worksFor: { "@id": `${SITE_ORIGIN}/#organization` },
    hasCredential: m.credentials.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      credentialCategory: c,
    })),
  }));
}

export const Route = createFileRoute("/ueber-uns")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => ({
    ...localizedHead("/ueber-uns", loaderData?.lang),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema("/ueber-uns", loaderData?.lang)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          medicalWebPageSchema("/ueber-uns", loaderData?.lang, {
            specialty: "Endocrine",
            aspect: "Overview",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(articleSchema("/ueber-uns", loaderData?.lang, { section: "About" })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(aboutPageSchema(loaderData?.lang)),
      },
      ...teamPersonSchemas(loaderData?.lang).map((p) => ({
        type: "application/ld+json" as const,
        children: JSON.stringify(p),
      })),
    ],
  }),
  component: AboutPage,
});

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function disciplineMeta(d: Discipline) {
  switch (d) {
    case "medicine":
      return { Icon: Stethoscope, tone: "bg-indigo/15 text-indigo" };
    case "science":
      return { Icon: Microscope, tone: "bg-emerald/15 text-emerald" };
    case "chemistry":
      return { Icon: FlaskConical, tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" };
    case "pharma":
      return { Icon: HeartPulse, tone: "bg-rose-500/15 text-rose-600 dark:text-rose-400" };
  }
}

function TeamCard({ member, lang, index }: { member: Member; lang: Lang; index: number }) {
  const de = lang === "de";
  const { Icon, tone } = disciplineMeta(member.discipline);
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.05 }}
      className="card-elevated card-hover flex h-full flex-col gap-4 p-6"
    >
      <div className="flex items-start gap-4">
        <div
          aria-hidden
          className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gradient-to-br from-indigo/20 to-indigo/5 font-semibold text-indigo"
        >
          {initials(member.name)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <span className={`inline-grid h-5 w-5 place-items-center rounded ${tone}`}>
              <Icon className="h-3 w-3" />
            </span>
            <span>
              {member.country === "DE"
                ? "🇩🇪 Deutschland"
                : member.country === "TH"
                ? "🇹🇭 Thailand"
                : "🇬🇧 Großbritannien"}
            </span>
          </div>
          <h3 className="mt-1 font-display text-lg leading-tight text-foreground">
            {member.honorific} {member.name}
          </h3>
          <p className="text-sm font-medium text-foreground/80">
            {de ? member.role.de : member.role.en}
          </p>
          <p className="text-xs text-muted-foreground">{de ? member.city.de : member.city.en}</p>
        </div>
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">
        {de ? member.bio.de : member.bio.en}
      </p>

      <div className="mt-auto space-y-2 border-t border-border/60 pt-3">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {de ? "Schwerpunkt" : "Focus"}
        </p>
        <p className="text-sm text-foreground/90">{de ? member.focus.de : member.focus.en}</p>
        <ul className="flex flex-wrap gap-1.5 pt-1">
          {member.credentials.map((c) => (
            <li
              key={c}
              className="rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
            >
              {c}
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

function AboutPage() {
  const { lang } = useI18n();
  const de = lang === "de";

  const copy = {
    eyebrow: de ? "Über uns" : "About us",
    h1: de
      ? "Ein deutsch-thailändisches Team für Retatrutid in Bangkok"
      : "A German-Thai team for Retatrutide in Bangkok",
    lead: de
      ? "Wir sind Ärzte, Wissenschaftler und Chemiker aus Deutschland und Thailand. Gemeinsam machen wir die neue GLP-1/GIP/Glucagon-Triple-Agonisten-Generation über eine Bangkoker Partnerklinik zugänglich – ärztlich begleitet, im behördlich genehmigten Studien­protokoll und mit persönlicher Betreuung auf Deutsch und Englisch."
      : "We are physicians, scientists and chemists from Germany and Thailand. Together we make the new GLP-1/GIP/glucagon triple-agonist generation available through a Bangkok partner clinic — medically supervised, under an authorised clinical protocol and with personal care in German and English.",
    stats: [
      {
        n: "20+",
        label: de ? "Ärzte & Wissenschaftler im Netzwerk" : "Physicians & scientists in the network",
      },
      {
        n: de ? "3 Länder" : "3 countries",
        label: de ? "Deutschland · Thailand · UK" : "Germany · Thailand · UK",
      },
      { n: "DE / EN", label: de ? "Betreuungssprachen" : "Languages of care" },
      { n: "Thai FDA (อย.)", label: de ? "Genehmigtes Studienprotokoll" : "Authorised trial protocol" },
    ],
    missionTitle: de ? "Unsere Mission" : "Our mission",
    missionBody: de
      ? "Adipositas ist eine chronische, medizinisch behandelbare Erkrankung. Wir sind überzeugt: Menschen mit metabolischem Syndrom, Insulinresistenz oder therapieresistentem Übergewicht sollten Zugang zur wirksamsten heute verfügbaren Behandlung haben – auch bevor Retatrutid (LY3437943) 2026/27 in der EU regulär zugelassen ist. Dieser Zugang muss ärztlich begleitet, transparent und pharmazeutisch sauber sein."
      : "Obesity is a chronic, medically treatable disease. We believe: people with metabolic syndrome, insulin resistance or treatment-resistant obesity should have access to the most effective therapy available today — even before Retatrutide (LY3437943) receives regular EU approval in 2026/27. That access must be medically supervised, transparent and pharmaceutically clean.",
    valuesTitle: de ? "Woran wir uns messen lassen" : "What we hold ourselves to",
    values: [
      {
        Icon: ShieldCheck,
        title: de ? "Ärztliche Aufsicht" : "Physician supervision",
        body: de
          ? "Jede Behandlung beginnt mit Anamnese, Labor und BMI-Bewertung durch einen der Ärzte des Teams. Fortsetzung nur bei medizinischer Eignung – Ja/Nein-Empfehlung ohne Verkaufsdruck."
          : "Every treatment starts with medical history, labs and a BMI assessment by one of the team's physicians. Continuation only when medically appropriate — a clear yes/no with no sales pressure.",
      },
      {
        Icon: FlaskConical,
        title: de ? "Pharmazeutische Sauberkeit" : "Pharmaceutical cleanliness",
        body: de
          ? "Chargenweise HPLC/MS-Reinheitsanalytik, dokumentierte Kühlkette 2–8 °C und Endotoxin-Testung durch einen promovierten Peptidchemiker – nicht durch den Verkäufer."
          : "Batch-level HPLC/MS purity testing, documented 2–8 °C cold chain and endotoxin screening by a PhD peptide chemist — not by the seller.",
      },
      {
        Icon: BookOpen,
        title: de ? "Wissenschaftliche Transparenz" : "Scientific transparency",
        body: de
          ? "Wir zitieren TRIUMPH-1 bis TRIUMPH-4 und veröffentlichte NEJM-Daten – keine Marketing-Behauptungen. Nebenwirkungen und Kontraindikationen werden explizit besprochen."
          : "We cite TRIUMPH-1 to TRIUMPH-4 and published NEJM data — not marketing claims. Side effects and contraindications are addressed explicitly.",
      },
      {
        Icon: Globe2,
        title: de ? "Zweisprachige Begleitung" : "Bilingual continuity",
        body: de
          ? "Vom deutschen Erstgespräch bis zur englischen Verlaufskontrolle in Bangkok: dieselben Ansprechpartner, ein gemeinsames Protokoll, keine Sprachbarrieren."
          : "From the German initial consult to the English follow-up in Bangkok: the same points of contact, one shared protocol, no language barriers.",
      },
    ],
    teamTitle: de ? "Unser Team" : "Our team",
    teamLead: de
      ? "Endokrinologen, Adipositas-Mediziner, klinische Pharmakologen und Peptidchemiker – jede Behandlung wird von benannten Personen verantwortet, nicht von einer anonymen Plattform."
      : "Endocrinologists, obesity physicians, clinical pharmacologists and peptide chemists — every treatment is owned by named people, not by an anonymous platform.",
    complianceTitle: de ? "Regulatorik & Standards" : "Regulation & standards",
    complianceBody: de
      ? "Die Behandlung findet in Bangkok unter der Studien­genehmigung der Thai FDA (อย.) statt. Retatrutid ist in Deutschland, Österreich und der Schweiz aktuell nicht regulär zugelassen; die reguläre EU-Zulassung wird nach Abschluss von TRIUMPH-1 im Zeitraum 2026/27 erwartet. Wir liefern keine Behandlung nach Deutschland und stellen keine Rezepte für den Selbstbezug aus."
      : "Treatment takes place in Bangkok under the Thai FDA (อย.) trial authorisation. Retatrutide is currently not regularly approved in Germany, Austria or Switzerland; regular EU approval is expected after TRIUMPH-1 concludes in 2026/27. We do not ship treatment to Germany and do not issue prescriptions for self-supply.",
    disclaimer: de
      ? "Diese Seite dient rein informativen Zwecken. Sie ersetzt keine individuelle ärztliche Beratung."
      : "This page is for informational purposes only. It does not replace individual medical advice.",
  } as const;


  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:py-20">
      {/* Hero */}
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">{copy.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {copy.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{copy.lead}</p>
      </div>

      {/* Stats strip */}
      <dl className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
        {copy.stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl border border-border bg-card p-4 text-center"
          >
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {s.label}
            </dt>
            <dd className="mt-1 font-display text-xl text-foreground md:text-2xl">{s.n}</dd>
          </div>
        ))}
      </dl>

      {/* Mission */}
      <section className="mt-16 rounded-2xl border border-border bg-card p-6 md:p-10">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-indigo/15 text-indigo">
            <GraduationCap className="h-5 w-5" />
          </span>
          <h2 className="font-display text-2xl text-foreground md:text-3xl">{copy.missionTitle}</h2>
        </div>
        <p className="mt-4 max-w-3xl leading-relaxed text-muted-foreground">{copy.missionBody}</p>
      </section>

      {/* Values */}
      <section className="mt-16">
        <h2 className="font-display text-2xl text-foreground md:text-3xl">{copy.valuesTitle}</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {copy.values.map((v) => (
            <div key={v.title} className="card-elevated flex gap-4 p-5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo/15 text-indigo">
                <v.Icon className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-semibold text-foreground">{v.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="mt-16">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl text-foreground md:text-3xl">{copy.teamTitle}</h2>
          <p className="mt-3 text-muted-foreground">{copy.teamLead}</p>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {TEAM.map((m, i) => (
            <TeamCard key={m.name} member={m} lang={lang} index={i} />
          ))}
        </div>
      </section>

      {/* Compliance */}
      <section className="mt-16 rounded-2xl border border-border bg-card p-6 md:p-10">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <h2 className="font-display text-2xl text-foreground md:text-3xl">
            {copy.complianceTitle}
          </h2>
        </div>
        <p className="mt-4 max-w-3xl leading-relaxed text-muted-foreground">{copy.complianceBody}</p>
        <p className="mt-4 text-xs text-muted-foreground">{copy.disclaimer}</p>
      </section>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/wissenschaft", "/studien", "/faq"]} />
    </div>
  );
}
